import { useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';

import {
  WeekHeaderRow,
  WeekHeaderCell,
  WeekHeaderBadge,
  WeekGrid,
  WeekContentContainer,
  TimeColumn,
  TimeCell,
  DayColumn,
  DayCellWithCursor,
  WeekEventDynamicWithZIndex,
  CurrentTimeIndicatorDynamic,
  CurrentTimeLabel,
  WeekEventContentDynamic,
  WeekEventNameTextWithSize,
  WeekEventTimeTextWithSize,
  WeekEventShortNameText,
  WeekEventStatusContainer,
  WeekEventEmailProblemIcon
} from '../../../Css/StyledCalendarWeek.js';

import {
  isSameDay,
  isSameDayString,
  formatCurrentTime,
  isCurrentDay,
  isEventInPast,
} from '../../../../utils/calendarUtils.js';

import {
  getNotificationProblemTooltip,
  shouldShowNotificationProblemIndicator
} from '../../../../utils/notificationCalendarUtils.js';

import {
  DAY_TIME_SLOTS,
  getAppointmentCalendarColor,
  getAppointmentCalendarTooltip
} from '../../../../constants/calendarConstants.js';

import StatusDropdown from '../StatusDropdown.js';
import { PlusIcon } from '../CalendarIcons.js';

const DEFAULT_VISIBLE_TIME_RANGES = [
  {
    start: '06:00',
    end: '24:00'
  }
];

// Convierte una hora en formato "HH:mm" a minutos totales.
// Sirve para comparar horarios de forma segura.
// Ejemplo: "14:30" se convierte en 870 minutos.
const timeToMinutes = (time) => {
  if (!time) {
    return 0;
  }

  const [hours, minutes = 0] = time.split(':').map(Number);

  return (hours * 60) + minutes;
};

// Normaliza los rangos visibles configurados por el usuario.
// Importante: el horario "hasta" se toma como inclusivo.
// Ejemplo: si el usuario elige 08:00 a 10:00,
// se muestran 08:00, 09:00 y 10:00.
const normalizeVisibleRanges = (calendarViewSettings) => {
  const ranges = calendarViewSettings?.visibleTimeRanges;

  if (!Array.isArray(ranges) || ranges.length === 0) {
    return DEFAULT_VISIBLE_TIME_RANGES;
  }

  return ranges
    .filter((range) => range?.start && range?.end && range.start <= range.end)
    .map((range) => {
      const startMinutes = timeToMinutes(range.start);
      const endInclusiveMinutes = timeToMinutes(range.end);

      return {
        start: range.start,
        end: range.end,
        startMinutes,
        endMinutes: endInclusiveMinutes + 60
      };
    })
    .sort((rangeA, rangeB) => rangeA.startMinutes - rangeB.startMinutes);
};

// Revisa si una hora queda dentro de alguno de los rangos visibles.
// Se usa para decidir qué horarios mostrar en la grilla.
const isTimeInsideVisibleRanges = (time, visibleRanges) => {
  const timeMinutes = timeToMinutes(time);

  return visibleRanges.some((range) => (
    timeMinutes >= range.startMinutes &&
    timeMinutes < range.endMinutes
  ));
};

// Filtra los horarios de la grilla usando la configuración del usuario.
// Ejemplo: si configuró 14:00 a 20:00,
// devuelve solo esos horarios visibles.
const getVisibleTimeSlots = (visibleRanges) => {
  return DAY_TIME_SLOTS.filter((timeSlot) =>
    isTimeInsideVisibleRanges(timeSlot, visibleRanges)
  );
};

// Convierte el inicio y fin de un turno a minutos.
// Sirve para calcular si el turno queda dentro o fuera del rango visible.
const getAppointmentMinutes = (appointment) => {
  return {
    startMinutes: timeToMinutes(appointment.hora_inicio),
    endMinutes: timeToMinutes(appointment.hora_fin)
  };
};

// Revisa si un turno debe verse según la configuración horaria.
// Por ahora usamos la hora de inicio del turno.
// Ejemplo: si la vista muestra 14:00 a 20:00 y el turno empieza a las 10:00,
// queda oculto.
const isAppointmentInsideVisibleRanges = (appointment, visibleRanges) => {
  const { startMinutes } = getAppointmentMinutes(appointment);

  return visibleRanges.some((range) => (
    startMinutes >= range.startMinutes &&
    startMinutes < range.endMinutes
  ));
};

// Formatea una fecha YYYY-MM-DD a DD/MM/YYYY.
// Se usa dentro del warning de turnos ocultos.
const formatDisplayDate = (dateString) => {
  if (!dateString) {
    return '';
  }

  const [year, month, day] = dateString.split('-');

  if (!year || !month || !day) {
    return dateString;
  }

  return `${day}/${month}/${year}`;
};

// Arma el mensaje del SweetAlert cuando hay turnos ocultos.
// Muestra cantidad y algunos ejemplos para que el usuario entienda
// que los turnos no desaparecieron, solo están fuera del horario visible.
const getHiddenAppointmentsMessage = (hiddenAppointments) => {
  if (!hiddenAppointments.length) {
    return '';
  }

  const cantidad = hiddenAppointments.length;

  const ejemplos = hiddenAppointments
    .slice(0, 5)
    .map((appointment) => {
      const paciente =
        appointment.paciente_nombre_completo ||
        `${appointment.paciente_nombre || ''} ${appointment.paciente_apellido || ''}`.trim() ||
        'Paciente sin nombre';

      return `• ${formatDisplayDate(appointment.fecha_turno)} ${appointment.hora_inicio} - ${paciente}`;
    })
    .join('\n');

  const textoCantidad =
    cantidad === 1
      ? 'Hay 1 turno fuera del horario visible.'
      : `Hay ${cantidad} turnos fuera del horario visible.`;

  const textoEjemplos = ejemplos
    ? `\n\nTurnos ocultos:\n${ejemplos}`
    : '';

  const textoFinal =
    cantidad > 5
      ? '\n\nSe muestran solo los primeros 5.'
      : '';

  return `${textoCantidad}\n\nNo se borraron ni desaparecieron. Solo están ocultos por la configuración de vista.${textoEjemplos}${textoFinal}\n\nPara verlos, tocá la ruedita ⚙ y ampliá el horario visible o elegí “Mostrar todo el día”.`;
};

// Escapa texto antes de mostrarlo como HTML dentro de SweetAlert.
// Evita que un nombre con caracteres raros rompa el mensaje.
const escapeHtml = (value) => {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};

// Calcula cuántos minutos visibles hay antes de determinada hora.
// Esto es clave cuando hay rangos partidos.
// Ejemplo: 08:00-12:00 y 16:00-22:00.
// La hora 16:00 no empieza abajo de 16 horas reales,
// sino después de las 4 horas visibles del primer rango.
const getVisibleMinutesBeforeTime = (time, visibleRanges) => {
  const timeMinutes = timeToMinutes(time);
  let accumulatedMinutes = 0;

  for (const range of visibleRanges) {
    if (timeMinutes >= range.startMinutes && timeMinutes < range.endMinutes) {
      return accumulatedMinutes + (timeMinutes - range.startMinutes);
    }

    accumulatedMinutes += range.endMinutes - range.startMinutes;
  }

  return accumulatedMinutes;
};

// Calcula la posición vertical de un turno o de la hora actual
// dentro de la grilla visible.
const getVisibleEventPosition = (horaInicio, visibleRanges, slotHeight) => {
  const visibleMinutesBefore = getVisibleMinutesBeforeTime(
    horaInicio,
    visibleRanges
  );

  return (visibleMinutesBefore / 60) * slotHeight;
};

// Calcula la altura visual de un turno según su duración.
// Se deja un mínimo de 15 minutos para que un turno corto no desaparezca.
const getVisibleEventHeight = (horaInicio, horaFin, slotHeight) => {
  const durationMinutes = Math.max(
    timeToMinutes(horaFin) - timeToMinutes(horaInicio),
    15
  );

  return (durationMinutes / 60) * slotHeight;
};

// Calcula la posición de la hora actual solamente si la hora actual
// está dentro de los rangos visibles configurados por el usuario.
// Si la hora actual está fuera del rango visible, devuelve -1.
const getCurrentTimePositionForVisibleRanges = (visibleRanges, slotHeight) => {
  const now = new Date();

  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  if (!isTimeInsideVisibleRanges(currentTime, visibleRanges)) {
    return -1;
  }

  return getVisibleEventPosition(currentTime, visibleRanges, slotHeight);
};

const WeekView = ({
  weekDays,
  selectedDate,
  weekAppointments = [],
  onTimeSlotClick,
  onStatusChange,
  onAppointmentClick,
  onAddAppointment,
  onOpenSettings,
  calendarViewSettings
}) => {
  // Referencia al contenedor que scrollea internamente.
  // Acá se mueve la grilla cuando activamos "posicionarme en la hora actual".
  const scrollContainerRef = useRef(null);

  // Referencia al header de días de la semana.
  // La usamos para calcular mejor el autoscroll, porque ese header ocupa alto
  // dentro del mismo contenedor scrolleable.
  const weekHeaderRef = useRef(null);

  const [openDropdownId, setOpenDropdownId] = useState(null);

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  // Alto disponible real para la grilla de horarios.
  // Lo usamos para que en pantallas grandes las filas se estiren
  // y no quede espacio blanco abajo.
  const [availableGridHeight, setAvailableGridHeight] = useState(0);

  // Convierte la configuración guardada en rangos seguros para usar.
  const visibleRanges = useMemo(
    () => normalizeVisibleRanges(calendarViewSettings),
    [calendarViewSettings]
  );

  // Calcula los horarios que se muestran en la columna izquierda.
  const visibleTimeSlots = useMemo(
    () => getVisibleTimeSlots(visibleRanges),
    [visibleRanges]
  );

  // Calcula los turnos que existen en la semana pero quedan fuera del horario visible.
  // Ejemplo: si la vista muestra de 14:00 a 20:00 y hay un turno a las 10:00,
  // ese turno no se ve en la grilla, pero queda guardado acá para mostrar el warning ⚠.
  const hiddenAppointments = useMemo(() => {
    return weekAppointments.filter((appointment) => {
      if (!appointment?.hora_inicio) {
        return false;
      }

      return !isAppointmentInsideVisibleRanges(appointment, visibleRanges);
    });
  }, [weekAppointments, visibleRanges]);

  const hasHiddenAppointments = hiddenAppointments.length > 0;

  // Altura mínima de cada bloque horario.
  // En pantallas muy chicas usamos un mínimo menor.
  // En escritorio/notebook usamos 66.67 como base.
  const minSlotHeight = windowWidth <= 768 ? 55 : 66.67;

  // Calcula cuánto debería medir cada fila para ocupar todo el alto disponible.
  // Si hay mucho espacio, las filas se estiran.
  // Si hay poco espacio, se mantiene el mínimo y aparece scroll interno.
  const dynamicSlotHeight =
    visibleTimeSlots.length > 0 && availableGridHeight > 0
      ? availableGridHeight / visibleTimeSlots.length
      : minSlotHeight;

  // Altura final de cada bloque horario.
  // Nunca baja del mínimo, pero puede crecer en monitores grandes.
  const slotHeight = Math.max(minSlotHeight, dynamicSlotHeight);

  // Alto total real de la grilla visible.
  const gridHeight = visibleTimeSlots.length * slotHeight;

  const [currentTimePosition, setCurrentTimePosition] = useState(
    getCurrentTimePositionForVisibleRanges(visibleRanges, slotHeight)
  );

  const currentTimeFormatted = formatCurrentTime();

  // Mide el alto disponible dentro del contenedor scrolleable.
  // Descuenta el header de días de la semana, porque ese header también ocupa espacio.
  // Esta medición permite que la grilla se adapte a pantallas chicas y grandes.
  const updateAvailableGridHeight = () => {
    if (!scrollContainerRef.current) {
      return;
    }

    const containerHeight = scrollContainerRef.current.clientHeight;
    const headerHeight = weekHeaderRef.current?.offsetHeight || 0;

    const nextAvailableGridHeight = Math.max(
      containerHeight - headerHeight,
      0
    );

    setAvailableGridHeight(nextAvailableGridHeight);
  };

  // Detecta si un turno dura 15 minutos o menos.
  // Esto se usa para achicar algunos textos internos del turno.
  const isShortAppointment = (horaInicio, horaFin) => {
    const inicio = new Date(`2000-01-01 ${horaInicio}`);
    const fin = new Date(`2000-01-01 ${horaFin}`);
    const duracionMinutos = (fin - inicio) / (1000 * 60);

    return duracionMinutos <= 15;
  };

  useEffect(() => {
    // Primero medimos el alto disponible real de la grilla.
    // Usamos requestAnimationFrame para esperar a que el navegador termine de pintar.
    requestAnimationFrame(() => {
      updateAvailableGridHeight();
    });

    // Calcula la posición actual de la línea roja usando los rangos visibles.
    const updatedCurrentTimePosition = getCurrentTimePositionForVisibleRanges(
      visibleRanges,
      slotHeight
    );

    setCurrentTimePosition(updatedCurrentTimePosition);

    // Cuando cambia el tamaño de la ventana, recalculamos ancho y alto disponible.
    const handleResize = () => {
      setWindowWidth(window.innerWidth);

      requestAnimationFrame(() => {
        updateAvailableGridHeight();
      });
    };

    window.addEventListener('resize', handleResize);

    // Si el usuario activó "posicionarme cerca de la hora actual",
    // intentamos ubicar la línea roja cerca del centro visual.
    if (
      calendarViewSettings?.autoScrollToCurrentTime !== false &&
      scrollContainerRef.current &&
      updatedCurrentTimePosition >= 0 &&
      updatedCurrentTimePosition <= gridHeight
    ) {
      const containerHeight = scrollContainerRef.current.clientHeight;
      const headerHeight = weekHeaderRef.current?.offsetHeight || 0;

      const targetPosition = updatedCurrentTimePosition + headerHeight;

      const desiredScrollPosition =
        targetPosition - (containerHeight * 0.52);

      const maxScrollTop =
        scrollContainerRef.current.scrollHeight -
        scrollContainerRef.current.clientHeight;

      const safeScrollPosition = Math.max(
        0,
        Math.min(desiredScrollPosition, maxScrollTop)
      );

      scrollContainerRef.current.scrollTo({
        top: safeScrollPosition,
        behavior: 'smooth'
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [
    visibleRanges,
    slotHeight,
    gridHeight,
    availableGridHeight,
    calendarViewSettings?.autoScrollToCurrentTime
  ]);

  // Abre el modal de creación en un día y horario específico.
  const handleTimeSlotClick = (dayDate, timeSlot) => {
    if (onTimeSlotClick) {
      onTimeSlotClick(dayDate, timeSlot);
    }
  };

  // Cambia el estado de un turno desde el dropdown.
  const handleStatusChange = (appointmentId, newStatus) => {
    if (onStatusChange) {
      onStatusChange(appointmentId, newStatus);
    }
  };

  // Abre el modal de edición del turno seleccionado.
  const handleAppointmentClick = (appointment) => {
    if (onAppointmentClick) {
      onAppointmentClick(appointment);
    }
  };

  // Botón + del cuadradito superior izquierdo.
  // Abre el modal de creación sin horario preseleccionado.
  const handleAddAppointmentClick = () => {
    if (onAddAppointment) {
      onAddAppointment();
    }
  };

  // Botón de configuración ⚙.
  // Abre el modal donde se configuran los horarios visibles.
  const handleOpenSettingsClick = () => {
    if (onOpenSettings) {
      onOpenSettings();
      return;
    }

    alert('La configuración de horarios visibles se agregará en el próximo paso.');
  };

  // Warning ⚠.
  // Muestra un SweetAlert con los turnos que están ocultos por el filtro horario.
  const handleHiddenAppointmentsWarningClick = async () => {
    const message = getHiddenAppointmentsMessage(hiddenAppointments);

    if (!message) {
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hay turnos ocultos',
      html: `
        <div style="text-align: left; white-space: pre-line; font-size: 14px;">
          ${escapeHtml(message)}
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Abrir configuración',
      cancelButtonText: 'Cerrar',
      confirmButtonColor: '#E7973C',
      cancelButtonColor: '#6B7280',
      allowOutsideClick: true,
      allowEscapeKey: true
    });

    if (result.isConfirmed && onOpenSettings) {
      onOpenSettings();
    }
  };

  // Controla qué dropdown de estado está abierto.
  // Sirve para darle z-index alto al turno que tiene el dropdown desplegado.
  const handleDropdownToggle = (appointmentId, isOpen) => {
    setOpenDropdownId(isOpen ? appointmentId : null);
  };

  // Marca visualmente si un slot horario ya pasó.
  // Aunque el slot haya pasado, se puede crear turno administrativo.
  // Esto solo puede afectar el estilo visual, no bloquea el click.
  const isPastTimeSlot = (dayDate, timeSlot) => {
    const now = new Date();
    const selectedDateTime = new Date(dayDate);
    const [hours, minutes] = timeSlot.split(':').map(Number);

    const endTime = new Date(selectedDateTime);
    endTime.setHours(hours, minutes + 30, 0, 0);

    return now > endTime;
  };

  return (
    <WeekContentContainer ref={scrollContainerRef}>
      <WeekHeaderRow ref={weekHeaderRef}>
        <WeekHeaderCell isFirst>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%'
            }}
          >
            <button
              type="button"
              aria-label="Agregar turno"
              title="Agregar turno"
              onClick={handleAddAppointmentClick}
              style={{
                width: '30px',
                height: '30px',
                border: 'none',
                borderRadius: '8px',
                background: 'hsl(210, 40%, 96%)',
                color: 'hsl(24, 85%, 60%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <PlusIcon size={16} />
            </button>

            <button
              type="button"
              aria-label="Configurar vista"
              title="Configurar vista"
              onClick={handleOpenSettingsClick}
              style={{
                width: '30px',
                height: '30px',
                border: 'none',
                borderRadius: '8px',
                background: 'hsl(210, 40%, 96%)',
                color: 'hsl(0, 0%, 35%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 600
              }}
            >
              ⚙
            </button>

            {hasHiddenAppointments && (
              <button
                type="button"
                aria-label="Hay turnos ocultos"
                title="Hay turnos ocultos fuera del horario visible"
                onClick={handleHiddenAppointmentsWarningClick}
                style={{
                  width: '30px',
                  height: '30px',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#FEF3C7',
                  color: '#B45309',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 700
                }}
              >
                ⚠
              </button>
            )}
          </div>
        </WeekHeaderCell>

        {weekDays.map((day, index) => {
          // El naranja representa HOY real, no la columna seleccionada.
          // Si navegás a otra semana, no debe quedar marcado el mismo día de semana.
          const isActive = isCurrentDay(day.date);

          return (
            <WeekHeaderCell
              key={`${day.name}-${day.date.toISOString()}`}
              isFirst={index === 0}
              active={isActive}
            >
              <span>{day.name}</span>
              <WeekHeaderBadge active={isActive}>{day.dateNumber}</WeekHeaderBadge>
            </WeekHeaderCell>
          );
        })}
      </WeekHeaderRow>

      <WeekGrid>
        <TimeColumn gridHeight={gridHeight}>
          {visibleTimeSlots.map((time) => (
            <TimeCell key={`time-${time}`} slotHeight={slotHeight}>
              {time}
            </TimeCell>
          ))}
        </TimeColumn>

        {weekDays.map((day, dayIndex) => (
          <DayColumn
            key={`day-column-${dayIndex}`}
            isFirst={dayIndex === 0}
            gridHeight={gridHeight}
          >
            {visibleTimeSlots.map((slot) => {
              const isPast = isPastTimeSlot(day.date, slot);

              return (
                <DayCellWithCursor
                  key={`slot-${dayIndex}-${slot}`}
                  onClick={() => handleTimeSlotClick(day.date, slot)}
                  isPast={isPast}
                  slotHeight={slotHeight}
                />
              );
            })}

            {currentTimePosition >= 0 &&
              currentTimePosition <= gridHeight &&
              isCurrentDay(weekDays[dayIndex].date) && (
                <CurrentTimeIndicatorDynamic top={currentTimePosition}>
                  <CurrentTimeLabel>{currentTimeFormatted}</CurrentTimeLabel>
                </CurrentTimeIndicatorDynamic>
              )}

            {weekAppointments
              .filter((appointment) => {
                return isSameDayString(
                  appointment.fecha_turno,
                  weekDays[dayIndex].date
                );
              })
              .filter((appointment) =>
                isAppointmentInsideVisibleRanges(appointment, visibleRanges)
              )
              .map((appointment) => {
                const isShort = isShortAppointment(
                  appointment.hora_inicio,
                  appointment.hora_fin
                );

                const isMobile = windowWidth <= 480;
                const isTablet = windowWidth > 480 && windowWidth <= 768;
                const isLaptop = windowWidth > 768 && windowWidth <= 1024;
                const isDesktop = windowWidth > 1024;

                const isLongAppointment = !isShortAppointment(
                  appointment.hora_inicio,
                  appointment.hora_fin
                );

                const pacienteNombreCompleto =
                  appointment.paciente_nombre_completo ||
                  `${appointment.paciente_nombre || ''} ${appointment.paciente_apellido || ''}`.trim() ||
                  'Paciente sin nombre';

                const nameParts = pacienteNombreCompleto.trim().split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.slice(1).join(' ') || '';

                const getFontSize = (baseSize) => {
                  if (isMobile) return baseSize - 1;
                  if (isTablet) return baseSize - 1;
                  if (isLaptop) return baseSize + 1;
                  if (isDesktop) return baseSize + 2;
                  return baseSize;
                };

                const getLongAppointmentFontSize = (baseSize) => {
                  if (isMobile) return baseSize - 1;
                  if (isTablet) return baseSize;
                  if (isLaptop) return baseSize + 1;
                  if (isDesktop) return baseSize + 2;
                  return baseSize;
                };

                const appointmentId = appointment.id_turno || appointment.id;
                const appointmentIsPast = isEventInPast(appointment);
                const showEmailProblem = shouldShowNotificationProblemIndicator(appointment);

                const appointmentTooltip = showEmailProblem
                  ? `${getAppointmentCalendarTooltip(appointment)} ${getNotificationProblemTooltip(appointment)}`
                  : getAppointmentCalendarTooltip(appointment);

                return (
                  <WeekEventDynamicWithZIndex
                    key={appointmentId}
                    variant={getAppointmentCalendarColor(appointment)}
                    title={appointmentTooltip}
                    aria-label={appointmentTooltip}
                    isPast={appointmentIsPast}
                    className={openDropdownId === appointmentId ? 'dropdown-open' : ''}
                    top={getVisibleEventPosition(
                      appointment.hora_inicio,
                      visibleRanges,
                      slotHeight
                    )}
                    height={getVisibleEventHeight(
                      appointment.hora_inicio,
                      appointment.hora_fin,
                      slotHeight
                    )}
                    isLongAppointment={isLongAppointment}
                    zIndex={
                      openDropdownId === appointmentId
                        ? 9999999
                        : openDropdownId
                        ? 1
                        : 'auto'
                    }
                    onClick={() => handleAppointmentClick(appointment)}
                    style={{ cursor: 'pointer' }}
                  >
                    <WeekEventContentDynamic
                      isLongAppointment={isLongAppointment}
                      fontSize={getFontSize(10)}
                      isTablet={isTablet}
                    >
                      {isLongAppointment ? (
                        <>
                          <WeekEventNameTextWithSize
                            fontSize={getLongAppointmentFontSize(9)}
                            lineHeight={isTablet ? '1.0' : '1.1'}
                          >
                            {lastName}
                          </WeekEventNameTextWithSize>

                          <WeekEventTimeTextWithSize
                            fontSize={getLongAppointmentFontSize(9)}
                            lineHeight={isTablet ? '1.0' : '1.1'}
                          >
                            {firstName}
                          </WeekEventTimeTextWithSize>
                        </>
                      ) : (
                        <WeekEventShortNameText>
                          {lastName ? `${lastName}, ${firstName}` : pacienteNombreCompleto}
                        </WeekEventShortNameText>
                      )}
                    </WeekEventContentDynamic>

                    {showEmailProblem && (
                      <WeekEventEmailProblemIcon
                        title={getNotificationProblemTooltip(appointment)}
                        aria-label={getNotificationProblemTooltip(appointment)}
                      >
                        ✉!
                      </WeekEventEmailProblemIcon>
                    )}
                    
                    <WeekEventStatusContainer
                      isLongAppointment={isLongAppointment}
                      isTablet={isTablet}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <StatusDropdown
                        currentStatus={appointment.estado}
                        onStatusChange={handleStatusChange}
                        appointmentId={appointmentId}
                        onToggle={handleDropdownToggle}
                        isSmall={isShort}
                      />
                    </WeekEventStatusContainer>
                  </WeekEventDynamicWithZIndex>
                );
              })}
          </DayColumn>
        ))}
      </WeekGrid>
    </WeekContentContainer>
  );
};

export default WeekView;